/**
 * Appends or extends a user-added mapping entry after new PDFs are merged.
 *
 * @module finalDocuments/utils/appendUserUpdatedMapping
 */
import { DocumentPageMapping } from "../../../data-model/medicalPaOrdersModel";

/** Display name for pages the user appended on Final Documents. */
export const USER_UPDATED_DOC_NAME = "Others";

/** Machine type for user-appended pages (`formatDocType` → "User Updated"). */
export const USER_UPDATED_DOC_TYPE = "user_updated";

/**
 * Builds an updated `document_page_mapping` after pages are appended.
 *
 * When `pagesAfter` is not greater than `pagesBefore`, the existing array is
 * returned unchanged. Consecutive user-added ranges coalesce into one
 * `user_updated` row instead of stacking duplicate "Others" entries.
 *
 * @input existing - Mapping rows already known for the merged packet
 * @input pagesBefore - Page count of the PDF on screen before the merge
 * @input pagesAfter - Page count of the merged PDF after the add
 * @return Mapping array including the user-updated tail, or `existing` if unchanged
 */
export const appendUserUpdatedMapping = (
  existing: DocumentPageMapping[],
  pagesBefore: number,
  pagesAfter: number,
): DocumentPageMapping[] => {
  if (pagesAfter <= pagesBefore || pagesBefore < 0 || pagesAfter < 1) {
    return existing;
  }

  const startPage = pagesBefore + 1;
  const pageCount = pagesAfter - pagesBefore;
  const lastIndex = existing.length - 1;
  const lastEntry = lastIndex >= 0 ? existing[lastIndex] : undefined;

  if (lastEntry?.doc_type === USER_UPDATED_DOC_TYPE) {
    const next = [...existing];
    next[lastIndex] = {
      ...lastEntry,
      start_page: lastEntry.start_page ?? startPage,
      end_page: pagesAfter,
      page_count: (lastEntry.page_count ?? 0) + pageCount,
    };
    return next;
  }

  const maxMergeOrder = existing.reduce(
    (max, entry) => Math.max(max, entry.merge_order ?? 0),
    0,
  );

  return [
    ...existing,
    {
      doc_name: USER_UPDATED_DOC_NAME,
      doc_type: USER_UPDATED_DOC_TYPE,
      start_page: startPage,
      end_page: pagesAfter,
      page_count: pageCount,
      merge_order: maxMergeOrder + 1,
    },
  ];
};
